/**
 * `ui.trigger_preview`: how long a user dwells on a trigger's what-if preview (the forecast if
 * the trigger happens, drawn while it is hovered — lib/timeline/hoverPreview.js). One event per
 * hover, recorded when it ends and only when it lasted `MIN_DWELL_MS`: a pointer passing over a
 * trigger is no look. Ids only — the forecast's and the trigger's (its id, or its place in the
 * lane when it has none), never its title.
 *
 * A pointer that slips off the trigger and back within `REENTER_MS` continues the same hover.
 * A hidden tab is no look: hiding the page ends the hover (recorded before the uploader's final
 * flush), and showing it again starts a new one if the preview is still up.
 */
import { recordUiEvent } from "../logRingBuffer";
import { idShaped } from "./uiIntents.js";

/** Below this a hover is a pass-over, not a look: nothing is recorded. */
export const MIN_DWELL_MS = 400;
/** A longer dwell is recorded as this (a pointer left resting on a trigger). */
export const MAX_DWELL_MS = 60_000;
/** Leaving and coming back to the same trigger within this is one hover. */
export const REENTER_MS = 300;

/**
 * A trigger's telemetry id: its own id when id-shaped, else `idx:<n>`, its place among the
 * triggers it was drawn with. Never its title.
 * @param {object|null} ev
 * @param {object[]} events the triggers drawn beside it
 */
export function previewTriggerId(ev, events) {
  if (!ev) return null;
  const own = idShaped(ev.id);
  if (own) return own;
  const at = Array.isArray(events) ? events.indexOf(ev) : -1;
  return at >= 0 ? `idx:${at}` : null;
}

/**
 * One hover at a time. `show(preview)` when a preview comes up, `hide(preview)` when it goes
 * (a hide for another preview than the one shown is ignored), `flush()` to end the current one
 * now. `pageHidden()` / `pageShown()` pause timing while the document is hidden.
 * @param {{record?: (e: object) => void, now?: () => number,
 *   setTimer?: typeof setTimeout, clearTimer?: typeof clearTimeout}} [deps]
 */
export function createTriggerPreviewDwell({
  record = recordUiEvent,
  now = () => (typeof performance !== "undefined" ? performance.now() : Date.now()),
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) {
  let current = null; // {key, preview, acc, shownAt, leaving}
  let shown = null; // the preview on screen, timed or not
  let paused = false;

  const keyOf = (p) => `${p.from}|${p.forecastId}|${p.triggerId}`;

  function end() {
    const c = current;
    if (!c) return;
    current = null;
    if (c.leaving) clearTimer(c.leaving);
    else c.acc += now() - c.shownAt;
    if (c.acc < MIN_DWELL_MS) return;
    try {
      record({
        event: "ui.trigger_preview",
        forecast_id: idShaped(c.preview.forecastId),
        trigger_id: c.preview.triggerId,
        from: c.preview.from,
        dwell_ms: Math.round(Math.min(c.acc, MAX_DWELL_MS)),
      });
    } catch {
      /* telemetry never breaks the hover */
    }
  }

  function show(preview) {
    if (!preview?.triggerId || !preview.from) return;
    const key = keyOf(preview);
    shown = { key, preview };
    if (paused) return;
    if (current?.key === key) {
      // Back within the re-entry window: the same hover goes on.
      if (current.leaving) {
        clearTimer(current.leaving);
        current.leaving = null;
        current.shownAt = now();
      }
      return;
    }
    end();
    current = { key, preview, acc: 0, shownAt: now(), leaving: null };
  }

  function hide(preview) {
    if (preview && shown?.key === keyOf(preview)) shown = null;
    if (!preview || !current || current.key !== keyOf(preview) || current.leaving) return;
    current.acc += now() - current.shownAt;
    current.leaving = setTimer(end, REENTER_MS);
  }

  function pageHidden() {
    paused = true;
    end();
  }

  function pageShown() {
    if (!paused) return;
    paused = false;
    if (shown) current = { ...shown, acc: 0, shownAt: now(), leaving: null };
  }

  return { show, hide, flush: end, pageHidden, pageShown };
}

/**
 * Pauses `tracker` while the page is hidden. Capture-phase listeners on the window run before
 * the uploader's own `visibilitychange` / `pagehide` handlers (services/telemetry.js), so the
 * ended hover is in the buffer for its final send.
 * @param {ReturnType<typeof createTriggerPreviewDwell>} tracker
 * @param {Window|undefined} win
 */
export function pauseWhileHidden(tracker, win) {
  if (!win?.addEventListener) return;
  const doc = win.document;
  win.addEventListener(
    "visibilitychange",
    () => (doc?.visibilityState === "hidden" ? tracker.pageHidden() : tracker.pageShown()),
    true,
  );
  win.addEventListener("pagehide", () => tracker.pageHidden(), true);
  win.addEventListener("pageshow", () => tracker.pageShown(), true);
}

/** The app's one tracker (a pointer hovers one trigger at a time). */
export const triggerPreviewDwell = createTriggerPreviewDwell();
pauseWhileHidden(triggerPreviewDwell, typeof window !== "undefined" ? window : undefined);
